import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { entryLink } from "@/lib/ui";
import { getChoices, setPreference } from "@/lib/choices";
import { useFilters } from "@/context/FiltersContext";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import { OfficialBox } from "@/components/OfficialControls";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Info, PlusCircle, Users, ListOrdered, ArrowLeftRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import useTradeConflicts from "@/lib/useTradeConflicts";
import TradeFinderDialog from "@/components/TradeFinderDialog";

const DLG = "bg-white text-[#14181F] border border-[#E4E7EB] max-w-lg";

function ActBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testid}
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${primary ? "bg-[#C8000F] text-white hover:bg-[#9E0008]" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

export default function RodeoActions({ rodeo, className = "" }) {
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null); // "info" | "count" | "trade"
  const conflict = useTradeConflicts().conflictFor(rodeo.id);
  const st = rodeo.status;
  return (
    <div className={`flex flex-nowrap items-center gap-1 overflow-x-auto ${className}`} data-testid={`rodeo-actions-${rodeo.id}`}>
      <ActBtn onClick={() => setDlg("info")} icon={Info} label="Info" testid={`ra-info-${rodeo.id}`} />
      {st !== "closed" && <ActBtn onClick={() => window.open(entryLink(rodeo), "_blank")} icon={PlusCircle} label="Enter" testid={`ra-enter-${rodeo.id}`} primary />}
      {st === "open" && <ActBtn onClick={() => setDlg("count")} icon={Users} label="Count" testid={`ra-count-${rodeo.id}`} />}
      <ActBtn onClick={() => nav(`/rodeo/${rodeo.id}?tab=draw`)} icon={ListOrdered} label="Draw" testid={`ra-draw-${rodeo.id}`} />
      {conflict && <ActBtn onClick={() => setDlg("trade")} icon={ArrowLeftRight} label="Trade" testid={`ra-trade-${rodeo.id}`} primary={!!conflict.conflicts_with?.length} />}
      <OfficialBox rodeoId={rodeo.id} />

      <RodeoInfoDialog data={dlg === "info" ? rodeo : null} onClose={() => setDlg(null)} />
      <CountDialog data={dlg === "count" ? rodeo : null} onClose={() => setDlg(null)} />
      <TradeFinderDialog rodeo={dlg === "trade" ? rodeo : null} conflict={conflict} onClose={() => setDlg(null)} />
    </div>
  );
}

function EnterDialog({ data, onClose }) {
  const [choices, setLocalChoices] = useState(getChoices());
  useEffect(() => { if (data) setLocalChoices(getChoices()); }, [data]);
  if (!data) return <Dialog open={false} onOpenChange={onClose}><DialogContent className={DLG} /></Dialog>;
  const ch = choices[data.id] || {};
  const set = (pid, n) => { setLocalChoices(setPreference(data.id, pid, n)); toast.success(`Set ${n === 1 ? "1st" : "2nd"} preference`); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="ra-enter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Enter · {data.name}</DialogTitle>
          <DialogDescription>Plan your 1st &amp; 2nd preference, then head to the entry site to lock it in.</DialogDescription>
        </DialogHeader>
        {(data.performances || []).length > 0 ? (
          <div className="space-y-2 max-h-[42vh] overflow-y-auto">
            {data.performances.map((p) => {
              const is1 = ch.pref1 === p.id, is2 = ch.pref2 === p.id;
              return (
                <div key={p.id} className={`flex items-center justify-between py-2 px-3 rounded-md border ${is1 ? "bg-[#DCEFC4] border-[#8CC152]" : "bg-[#F7F8FA] border-transparent"}`}>
                  <div>
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-xs text-slate-500">{fmtDate(p.date)} · {p.time}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => set(p.id, 1)} data-testid={`ra-enter-pref1-${p.id}`} className={`text-[11px] px-2 py-1 rounded font-semibold ${is1 ? "bg-[#C8000F] text-white" : "bg-white border border-[#D7DBE0] text-slate-600"}`}>1st</button>
                    <button onClick={() => set(p.id, 2)} data-testid={`ra-enter-pref2-${p.id}`} className={`text-[11px] px-2 py-1 rounded font-semibold ${is2 ? "bg-[#C8000F] text-white" : "bg-white border border-[#D7DBE0] text-slate-600"}`}>2nd</button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-slate-500">Performance schedule not posted yet.</p>}
        <a href={entryLink(data)} target="_blank" rel="noreferrer" data-testid={`ra-enter-link-${data.id}`}
          className="mt-1 inline-flex items-center justify-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg">
          <ExternalLink className="w-4 h-4" /> Go to entry site to enter
        </a>
      </DialogContent>
    </Dialog>
  );
}

function CountDialog({ data, onClose }) {
  const { events: myEvents } = useFilters();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (data) { setCounts(null); api.get(`/rodeos/${data.id}/count`).then((r) => setCounts(r.data)).catch(() => setCounts({})); }
  }, [data]);
  const evList = data ? (data.events || []).filter((ev) => myEvents.size === 0 || myEvents.has(ev)) : [];
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="ra-count-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Entry Count · {data.name}</DialogTitle>
              <DialogDescription>How the field is filling up per performance — your odds of getting your preference.</DialogDescription>
            </DialogHeader>
            {!counts ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div> : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {(data.performances || []).map((p) => (
                  <div key={p.id} className="rounded-md border border-[#EEF0F2] p-3">
                    <div className="text-sm font-semibold mb-2 flex items-center justify-between"><span>{p.label}</span><span className="text-xs text-slate-400">{fmtDate(p.date)}</span></div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {evList.map((ev) => (
                        <div key={ev} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-[#F7F8FA]">
                          <span className="text-slate-500 truncate mr-2">{ev}</span>
                          <span className="text-[#C8000F] font-bold">{counts[p.id]?.[ev] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
